import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { Body, Button, Caption, ErrorState, Row, Stack } from '@leanin/ui';

import { LoadingState } from '@/components/states/index';
import { useAdminBroadcastAudienceTargets } from '@/lib/queries/admin';
import {
  useUpdateBroadcast,
  type UpdateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useUpdateGroupBroadcast,
  type UpdateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { useStepUp } from '@/lib/use-step-up';
import {
  broadcastKeys,
  type AdminBroadcastDetail,
  type BroadcastScope,
} from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  EMPTY_AUDIENCE_STATE,
  audiencePatch,
  audienceStateFromSpec,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import {
  contentDraftFromDetail,
  contentDraftGap,
  contentPatchFromDraft,
  type BroadcastContentDraft,
} from './broadcast-content-draft';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

export interface BroadcastEditFormProps {
  readonly audience: BroadcastAudienceContext;
  readonly draft: AdminBroadcastDetail;
}

// Platform drafts can change their audience, so the form opens on the stored
// one — hand-picked ids are resolved to display rows first. Group drafts
// never edit the audience (the PATCH omits it), so there is nothing to load.
export function BroadcastEditForm(props: BroadcastEditFormProps) {
  return props.audience.mode === 'platform' ? (
    <PlatformBroadcastEditForm {...props} />
  ) : (
    <BroadcastEditFormBody {...props} initialAudienceState={EMPTY_AUDIENCE_STATE} />
  );
}

function PlatformBroadcastEditForm(props: BroadcastEditFormProps) {
  const targetsQ = useAdminBroadcastAudienceTargets(props.draft.audienceSpec);
  if (targetsQ.isError) {
    return <ErrorState title="We couldn't load that broadcast." body="Try again in a moment." />;
  }
  if (targetsQ.data === undefined) {
    return <LoadingState rows={3} label="Loading broadcast" />;
  }
  return (
    <BroadcastEditFormBody
      {...props}
      initialAudienceState={audienceStateFromSpec(props.draft.audienceSpec, targetsQ.data)}
    />
  );
}


function BroadcastEditFormBody({
  audience,
  draft,
  initialAudienceState,
}: BroadcastEditFormProps & { readonly initialAudienceState: AudienceState }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const updatePlatform = useUpdateBroadcast();
  const updateGroup = useUpdateGroupBroadcast();
  const { runStepUp } = useStepUp();
  const tDm = useTranslations('Admin.broadcasts.dm');

  const [initialContent] = useState<BroadcastContentDraft>(() =>
    contentDraftFromDetail(draft, emptyBroadcastDoc),
  );
  const [content, setContent] = useState<BroadcastContentDraft>(initialContent);
  const [audienceState, setAudienceState] =
    useState<AudienceState>(initialAudienceState);
  const audienceTouched = audienceState !== initialAudienceState;
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const [version, setVersion] = useState(draft.version);

  const pending = updatePlatform.isPending || updateGroup.isPending;

  const scope: BroadcastScope =
    audience.mode === 'platform'
      ? { kind: 'platform' }
      : {
          kind: 'group',
          groupKind: audience.kind,
          groupId: audience.groupId,
        };

  const dirty =
    audienceTouched || JSON.stringify(content) !== JSON.stringify(initialContent);

  const guard = useUnsavedChangesGuard({ dirty });

  const gap = contentDraftGap(content);
  const minimumFieldsForSave = gap === null;

  // 'ok' completed; 'cancelled' = user dismissed the step-up reverification
  // prompt (platform send/schedule only); 'error' carries the failure code
  // (for version-conflict detection) + message.
  type SubmitResult =
    | { status: 'ok' }
    | { status: 'cancelled' }
    | { status: 'error'; code: string; message: string };

  const submit = async (
    mode: 'send' | 'schedule' | 'draft',
    scheduledIso?: string,
  ): Promise<SubmitResult> => {
    if (gap === 'subject') {
      return { status: 'error', code: 'validation', message: 'Subject is required.' };
    }
    if (gap === 'message') {
      return { status: 'error', code: 'validation', message: 'Message is required.' };
    }
    // 'send' = send now → schedule for the current instant. The server flips
    // status draft→scheduled off a non-null scheduledSendAt and the dispatcher
    // picks it up immediately (mirrors the composer's create-with-mode='send'
    // path). Previously 'send' fell through to `undefined`, so the PATCH
    // omitted scheduledSendAt and the draft's status never changed.
    const scheduledSendAt =
      mode === 'schedule'
        ? scheduledIso
        : mode === 'draft'
          ? null
          : new Date().toISOString();
    const fallback =
      mode === 'draft'
        ? 'Failed to save.'
        : mode === 'schedule'
          ? 'Failed to schedule.'
          : 'Failed to send.';

    // A platform-wide send (draft→scheduled) is step-up'd server-side (RBAC
    // Decision C); runStepUp opens Clerk's reverification modal and retries
    // once on success. Group-leader sends carry no step-up — runStepUp is a
    // transparent pass-through there (the server never returns the hint), so
    // both branches share one normalised outcome.
    const mutateOutcome =
      audience.mode === 'platform'
        ? await runStepUp(() => {
            const patch: UpdateBroadcastInput = {
              version,
              ...contentPatchFromDraft(content, draft.sendVia),
              ...audiencePatch(initialAudienceState, audienceState),
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updatePlatform.mutateAsync({ id: draft.id, patch });
          }, fallback)
        : await runStepUp(() => {
            // Group broadcasts are email; the Channel control is platform-only.
            const { email } = content;
            const input: UpdateGroupBroadcastInput = {
              kind: audience.kind,
              groupId: audience.groupId,
              broadcastId: draft.id,
              version,
              subject: email.subject.trim(),
              bodyDoc: email.bodyDoc,
              preheaderText: email.preheaderText.trim() || null,
              replyTo: email.replyTo.trim() || null,
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updateGroup.mutateAsync(input);
          }, fallback);

    if (mutateOutcome.status === 'cancelled') return { status: 'cancelled' };
    if (mutateOutcome.status === 'error') {
      return {
        status: 'error',
        code: mutateOutcome.code,
        message: mutateOutcome.message,
      };
    }
    setVersion(mutateOutcome.data.version);
    return { status: 'ok' };
  };

  const handleVersionConflict = async (code: string) => {
    if (/version_mismatch/i.test(code)) {
      setError('This broadcast was modified in another tab. Reloading…');
      await qc.invalidateQueries({
        queryKey: broadcastKeys.detail(scope, draft.id).queryKey,
      });
      return true;
    }
    return false;
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    setError(null);
    const result = await submit('send');
    if (result.status === 'ok') return navigateAway();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setError(result.message);
  };

  const handleSchedule = async (iso: string) => {
    setError(null);
    const result = await submit('schedule', iso);
    if (result.status === 'ok') return navigateAway();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setError(result.message);
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    const result = await submit('draft');
    setSavePending(false);
    if (result.status === 'ok') return guard.proceed();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setDialogError(result.message);
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        content={content}
        onContentChange={setContent}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        broadcastIdForPreview={draft.id}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" align="center" justify="space-between">
        <Caption>
          Version {version} · last updated{' '}
          {new Date(draft.createdAt).toLocaleString()}
        </Caption>
        <Row gap="md" align="center">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <ScheduleSendButton
            disabled={!minimumFieldsForSave}
            pending={pending}
            onSendNow={handleSendNow}
            onSchedule={handleSchedule}
          />
        </Row>
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          minimumFieldsForSave
            ? undefined
            : content.channel === 'dm'
              ? tDm('saveDisabledReason')
              : 'Subject and message are required to save as draft.'
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

